import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Loader2, 
  Search,
  Car,
  Clock,
  CreditCard,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Timer,
  MapPin,
  HelpCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';
const IDLE_TIMEOUT = 60;
const SITE_ID = "site_001"; // Default site

// Virtual Keyboard Component
function VirtualKeyboard({ value, onChange, onSubmit, loading }) {
  const keys = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'DEL']
  ];

  const handleKey = (key) => {
    if (loading) return;
    if (key === 'DEL') {
      onChange(value.slice(0, -1));
    } else if (value.length < 9) {
      onChange(value + key);
    }
  };

  return (
    <div className="bg-slate-800 rounded-2xl p-2 sm:p-4 w-full max-w-2xl mx-auto">
      {keys.map((row, rowIndex) => (
        <div key={rowIndex} className="flex justify-center gap-1 sm:gap-2 mb-1 sm:mb-2">
          {row.map((key) => (
            <button
              key={key}
              onClick={() => handleKey(key)}
              disabled={loading}
              className={`
                min-w-0 flex-1 max-w-[36px] sm:max-w-[44px] py-2.5 sm:py-4 rounded-lg sm:rounded-xl font-bold text-sm sm:text-lg transition-all
                ${key === 'DEL' 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-slate-700 hover:bg-slate-600 text-white'}
                ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              data-testid={`key-${key}`}
            >
              {key === 'DEL' ? '⌫' : key}
            </button>
          ))}
        </div>
      ))}
      <div className="flex justify-center mt-3 sm:mt-4">
        <button
          onClick={onSubmit}
          disabled={value.length < 5 || loading}
          className={`
            px-6 sm:px-12 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-base sm:text-xl flex items-center gap-2 sm:gap-3
            ${value.length >= 5 && !loading
              ? 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer'
              : 'bg-slate-600 text-slate-400 cursor-not-allowed'}
          `}
          data-testid="search-session-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
              <span className="hidden sm:inline">Searching...</span>
              <span className="sm:hidden">...</span>
            </>
          ) : (
            <>
              <Search className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="hidden sm:inline">FIND SESSION</span>
              <span className="sm:hidden">FIND</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Retry configuration for ANPR propagation delay handling
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export default function KioskSessionLookup() {
  const navigate = useNavigate();
  const [vehicleReg, setVehicleReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [searchState, setSearchState] = useState("idle"); // idle | searching | locating | found | not_found | confirmation | confirming | ambiguous
  const [retryCount, setRetryCount] = useState(0);
  const idleTimerRef = useRef(null);
  
  // Fuzzy match confirmation state (R9.106)
  const [confirmationData, setConfirmationData] = useState(null);

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  // Core API call - returns session data or throws
  const fetchSession = async (vrm) => {
    const response = await axios.get(`${API}/parking/session/${encodeURIComponent(vrm)}/calculate`);
    return {
      found: true,
      session_id: response.data.session_id,
      vehicle_registration: response.data.vehicle_registration,
      site_id: response.data.site_id,
      entry_time: response.data.entry_time,
      duration_hours: response.data.duration_hours,
      duration_display: response.data.duration_display,
      amount_due: response.data.amount_due,
      outstanding: response.data.amount_due,
      amount_paid: 0,
      has_exit_auth: false,
      is_grace_period: response.data.is_grace_period,
      is_whitelisted: response.data.is_whitelisted ?? false,
      whitelist_reason: response.data.whitelist_reason ?? null,
      // Fuzzy match fields (R9.106)
      confirmation_required: response.data.confirmation_required ?? false,
      match_type: response.data.match_type ?? "exact",
      matched_vrm: response.data.matched_vrm ?? response.data.vehicle_registration
    };
  };

  // Search with automatic retry for ANPR propagation delay
  const handleSearch = async (currentRetry = 0) => {
    const vrm = vehicleReg.trim().toUpperCase();
    
    if (!vrm || vrm.length < 5) {
      setError("Please enter a valid vehicle registration");
      return;
    }

    // Reset state for fresh search
    if (currentRetry === 0) {
      setLoading(true);
      setError(null);
      setSession(null);
      setConfirmationData(null);
      setSearchState("searching");
      setRetryCount(0);
    }

    try {
      const sessionData = await fetchSession(vrm);
      
      // Check if fuzzy confirmation is required (R9.106)
      if (sessionData.confirmation_required && sessionData.match_type === "fuzzy") {
        // Show confirmation UI instead of proceeding directly
        setConfirmationData({
          session_id: sessionData.session_id,
          vehicle_registration: vrm, // Driver's entered VRM
          matched_vrm: sessionData.matched_vrm, // ANPR/session VRM
          sessionData: sessionData
        });
        setSearchState("confirmation");
        setLoading(false);
        return;
      }
      
      // Exact match - proceed normally
      setSession(sessionData);
      setSearchState("found");
      setLoading(false);
    } catch (err) {
      console.error("Session lookup error:", err);
      
      // Check for MULTIPLE_POSSIBLE_MATCHES error (R9.106)
      if (err.response?.data?.error === "MULTIPLE_POSSIBLE_MATCHES" || 
          err.response?.data?.detail?.includes("MULTIPLE_POSSIBLE_MATCHES") ||
          err.response?.data?.code === "MULTIPLE_POSSIBLE_MATCHES") {
        setSearchState("ambiguous");
        setError(null);
        setLoading(false);
        return;
      }
      
      if (err.response?.status === 404) {
        // 404 - Session not found, attempt retry if under limit
        if (currentRetry < MAX_RETRIES) {
          // Show "locating" state and retry
          setSearchState("locating");
          setRetryCount(currentRetry + 1);
          
          // Wait and retry
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          return handleSearch(currentRetry + 1);
        } else {
          // All retries exhausted - show final not found state
          setSearchState("not_found");
          setError(null); // Clear error, we'll show custom not_found UI
          setLoading(false);
        }
      } else {
        // Non-404 error - show generic error
        setSearchState("idle");
        setError("Unable to connect. Please try again.");
        setLoading(false);
      }
    }
  };

  // Handle fuzzy match confirmation - YES (R9.106)
  const handleConfirmYes = async () => {
    if (!confirmationData) return;
    
    setSearchState("confirming");
    setLoading(true);
    
    try {
      // POST to confirm-vrm endpoint
      await axios.post(`${API}/parking/session/confirm-vrm`, {
        session_id: confirmationData.session_id,
        confirmed_vrm: confirmationData.vehicle_registration,
        matched_vrm: confirmationData.matched_vrm
      });
      
      // On success, continue to session/payment flow with confirmed VRM
      setSession({
        ...confirmationData.sessionData,
        vehicle_registration: confirmationData.vehicle_registration
      });
      setSearchState("found");
      setConfirmationData(null);
      setLoading(false);
    } catch (err) {
      console.error("VRM confirmation error:", err);
      setError("Unable to confirm registration. Please try again.");
      setSearchState("idle");
      setConfirmationData(null);
      setLoading(false);
    }
  };

  // Handle fuzzy match confirmation - NO (R9.106)
  const handleConfirmNo = () => {
    // Return to VRM entry, clear entered registration
    // No backend call, no database change
    setVehicleReg("");
    setConfirmationData(null);
    setSearchState("idle");
    setError(null);
    setSession(null);
  };

  // Reset and try again
  const handleTryAgain = () => {
    setVehicleReg("");
    setSession(null);
    setError(null);
    setSearchState("idle");
    setRetryCount(0);
    setConfirmationData(null);
  };

  // Format helpers
  const formatDuration = (hours) => {
    if (!hours) return "0h 0m";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "£0.00";
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status display
  const getStatusDisplay = () => {
    if (!session) return null;

    if (session.has_exit_auth) {
      return {
        icon: CheckCircle,
        color: "text-green-400",
        bg: "bg-green-500/10 border-green-500/30",
        label: "Exit Authorized",
        message: "You are authorized to exit the car park."
      };
    }

    if (session.outstanding === 0) {
      return {
        icon: CheckCircle,
        color: "text-green-400",
        bg: "bg-green-500/10 border-green-500/30",
        label: "Paid",
        message: "Your parking fee has been paid."
      };
    }

    if (session.outstanding > 0) {
      return {
        icon: CreditCard,
        color: "text-yellow-400",
        bg: "bg-yellow-500/10 border-yellow-500/30",
        label: "Payment Required",
        message: `Outstanding balance: ${formatCurrency(session.outstanding)}`
      };
    }

    return {
      icon: Clock,
      color: "text-blue-400",
      bg: "bg-blue-500/10 border-blue-500/30",
      label: "Active",
      message: "Your parking session is active."
    };
  };

  const status = getStatusDisplay();

  return (
    <div 
      data-testid="kiosk-session-lookup"
      className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800"
    >
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur border-b border-slate-700 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/kiosk")}
            className="flex items-center gap-2 text-slate-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-3">
            <Car className="w-6 h-6 text-blue-400" />
            <span className="text-white font-semibold">Check Parking Status</span>
          </div>
          <div className="w-16" /> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-6">
        {/* Vehicle Registration Input */}
        <div className="mb-6">
          <label className="text-slate-400 text-sm mb-2 block">Enter Vehicle Registration</label>
          <div className="bg-yellow-400 rounded-xl p-4">
            <div className="flex items-center justify-center gap-4">
              <div className="bg-blue-800 text-white px-3 py-1 rounded font-bold">GB</div>
              <input
                type="text"
                value={vehicleReg}
                onChange={(e) => {
                  setVehicleReg(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                  setError(null);
                  setSession(null);
                }}
                className="flex-1 text-center text-4xl font-mono font-bold text-slate-900 tracking-wider bg-transparent outline-none placeholder:text-slate-500"
                placeholder="AB12 CDE"
                maxLength={9}
                data-testid="vrm-input"
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {/* Fuzzy Match Confirmation UI (R9.106) */}
        {searchState === "confirmation" && confirmationData && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-8 mb-6" data-testid="confirmation-state">
            <div className="text-center mb-6">
              <HelpCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Please confirm your registration</h3>
              <p className="text-slate-300">
                We found a parking session that closely matches the registration you entered.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-6 mb-6">
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-2">You entered:</p>
                <p className="text-3xl font-mono font-bold text-white tracking-wider mb-4">
                  {confirmationData.vehicle_registration}
                </p>
                <p className="text-lg text-amber-300">
                  Is <span className="font-mono font-bold">{confirmationData.vehicle_registration}</span> your vehicle's correct registration?
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleConfirmYes}
                disabled={loading}
                className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                data-testid="confirm-yes-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-6 h-6" />
                    YES, CONTINUE
                  </>
                )}
              </button>
              <button
                onClick={handleConfirmNo}
                disabled={loading}
                className="flex-1 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-600/50 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                data-testid="confirm-no-btn"
              >
                <XCircle className="w-6 h-6" />
                NO, CHANGE
              </button>
            </div>
          </div>
        )}

        {/* Confirming state - while POST is in progress */}
        {searchState === "confirming" && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-8 mb-6 text-center" data-testid="confirming-state">
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Confirming your registration...</h3>
            <p className="text-blue-300">Please wait.</p>
          </div>
        )}

        {/* Ambiguous Match State (R9.106) - MULTIPLE_POSSIBLE_MATCHES */}
        {searchState === "ambiguous" && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-8 mb-6 text-center" data-testid="ambiguous-state">
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-4">
              We couldn't confidently match that registration.
            </h3>
            <p className="text-slate-300 mb-6">
              Please check it and enter it again.
            </p>
            <button
              onClick={handleTryAgain}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
              data-testid="try-again-btn"
            >
              <RefreshCw className="w-6 h-6" />
              Try Again
            </button>
          </div>
        )}

        {/* Locating Session State - shown during retries */}
        {searchState === "locating" && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-8 mb-6 text-center" data-testid="locating-state">
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Locating your session...</h3>
            <p className="text-blue-300 mb-2">
              Your entry may still be processing. Please wait.
            </p>
            <p className="text-slate-500 text-sm">
              Attempt {retryCount} of {MAX_RETRIES}
            </p>
          </div>
        )}

        {/* Session Not Found State - shown after all retries exhausted */}
        {searchState === "not_found" && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 mb-6 text-center" data-testid="not-found-state">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-3xl font-bold text-white mb-4">Free parking for you today.</h3>
            <button
              onClick={handleTryAgain}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 mt-6"
              data-testid="try-again-btn"
            >
              <RefreshCw className="w-6 h-6" />
              Check Another Vehicle
            </button>
          </div>
        )}

        {/* Session Results */}
        {session && (
          <div className={`border rounded-2xl p-6 mb-6 ${status.bg}`}>
            <div className="flex items-center gap-3 mb-4">
              <status.icon className={`w-8 h-8 ${status.color}`} />
              <div>
                <h3 className="text-xl font-bold text-white">{status.label}</h3>
                <p className={`text-sm ${status.color}`}>{status.message}</p>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-700">
                <span className="text-slate-400 flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  Vehicle
                </span>
                <span className="text-white font-mono font-bold">{session.vehicle_registration}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-slate-700">
                <span className="text-slate-400 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </span>
                <span className="text-white">Kyle Surface Car Park</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-slate-700">
                <span className="text-slate-400 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Entry Time
                </span>
                <span className="text-white">{formatTime(session.entry_time)}</span>
              </div>

              {/* P1 UX: Show Parking Status for whitelisted, Duration for others */}
              {session.is_whitelisted ? (
                <div className="flex flex-col py-2 border-b border-slate-700">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Parking Status
                    </span>
                    <span className="text-green-500 font-semibold">No payment required</span>
                  </div>
                  <p className="text-slate-500 text-sm mt-1 ml-6">
                    This vehicle is registered for free parking at this location.
                  </p>
                </div>
              ) : (
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400 flex items-center gap-2">
                    <Timer className="w-4 h-4" />
                    Duration
                  </span>
                  <span className="text-white font-medium">{session.duration_display || formatDuration(session.duration_hours)}</span>
                </div>
              )}

              <div className="flex justify-between items-center py-2">
                <span className="text-slate-400">Charges</span>
                <span className="text-white font-medium">{formatCurrency(session.amount_due)}</span>
              </div>

              {session.amount_paid > 0 && (
                <div className="flex justify-between items-center py-2 border-t border-slate-700">
                  <span className="text-green-400">Paid</span>
                  <span className="text-green-400 font-medium">-{formatCurrency(session.amount_paid)}</span>
                </div>
              )}

              {session.outstanding > 0 && (
                <div className="flex justify-between items-center py-3 bg-slate-900/50 rounded-lg px-3 -mx-1">
                  <span className="text-white font-semibold">Outstanding</span>
                  <span className="text-2xl font-bold text-yellow-400">{formatCurrency(session.outstanding)}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 space-y-3">
              {session.outstanding > 0 && !session.has_exit_auth && (
                <button
                  onClick={() => navigate("/kiosk/session-payment", { 
                    state: { vehicleReg: session.vehicle_registration, sessionData: session }
                  })}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                  data-testid="pay-before-exit-btn"
                >
                  <CreditCard className="w-6 h-6" />
                  Pay {formatCurrency(session.outstanding)} Before Exit
                </button>
              )}

              {(session.outstanding === 0 || session.has_exit_auth) && (
                <button
                  onClick={() => navigate("/kiosk/confirmation", { 
                    state: { type: "exit_ready", vehicleReg: session.vehicle_registration }
                  })}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                  data-testid="exit-ready-btn"
                >
                  <CheckCircle className="w-6 h-6" />
                  Ready to Exit
                </button>
              )}

              <button
                onClick={() => {
                  setSession(null);
                  setVehicleReg("");
                }}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="check-another-btn"
              >
                <RefreshCw className="w-5 h-5" />
                Check Another Vehicle
              </button>
            </div>
          </div>
        )}

        {/* Keyboard - show only when idle/searching and no session displayed */}
        {!session && searchState !== "locating" && searchState !== "not_found" && searchState !== "confirmation" && searchState !== "confirming" && searchState !== "ambiguous" && (
          <VirtualKeyboard
            value={vehicleReg}
            onChange={setVehicleReg}
            onSubmit={() => handleSearch(0)}
            loading={loading}
          />
        )}

        {/* Help text */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>Enter your vehicle registration to check your parking status</p>
          <p className="mt-2">Need help? Call 01292 833107</p>
        </div>
      </main>
    </div>
  );
}
